import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ActionType, ApplicationInsightEvent, GemisTenant } from '@app/shared/enums';
import { Disclaimer } from '@app/shared/models/dialog/terms-of-use-dialog-model';
import { AzureBlobStorageService } from '@app/shared/services/api/azure-blob-storage.service';
import { DisclaimerService } from '@app/shared/services/api/disclaimer.service';
import { UserService } from '@app/shared/services/api/user.service';
import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import { AuthService } from '@app/shared/services/auth.service';
import { GetServiceBusUserDetails } from '@app/shared/models/user';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { saveFile } from '@app/shared/utility';
import { environment } from '@env/environment';
import moment from 'moment';
import { Observable } from 'rxjs/internal/Observable';
import { map, mergeMap } from 'rxjs/operators';

@Component({
  selector: 'app-terms-of-use-dialog',
  templateUrl: './terms-of-use-dialog.component.html',
  styleUrls: ['./terms-of-use-dialog.component.scss'],
})
export class TermsOfUseDialogComponent implements OnInit {
  userLegalVersion: string;
  latestLegalVersion!: string;
  disclaimers$: Observable<Disclaimer[]>;
  user!: GetServiceBusUserDetails;
  constructor(
    private readonly overlayLoader: OverlayLoaderService,
    private readonly appInsightsServiceDirective: AppInsightsServiceDirective,
    private readonly authService: AuthService,
    private readonly disclaimerService: DisclaimerService,
    private readonly userService: UserService,
    private readonly matDialogRef: MatDialogRef<TermsOfUseDialogComponent>,
    private readonly blobStorageService: AzureBlobStorageService,
    private readonly userMunicipalitiesService: UserMunicipalitiesService,
  ) {
    this.userLegalVersion = this.authService.currentUser.legalDisclaimerVersion;

    this.disclaimers$ = this.disclaimerService.getAllDisclaimerVersions().pipe(
      map((disclaimers) => {
        disclaimers.forEach((disclaimer) => {
          disclaimer.deltaFile = `${environment.appLogicStaticWebsiteUrl}${environment.tenant}/${disclaimer.deltaFile}`;
          disclaimer.isMainDisclaimer = disclaimer.version === '1';
        });
        return disclaimers;
      }),
    );

    this.disclaimerService.getLatestDisclaimerVersion().subscribe((res) => {
      this.latestLegalVersion = res.version;
    });
  }

  ngOnInit() {
    this.userService
      .getUserServiceBusDetails(this.authService.currentUser.guid)
      .pipe(
        map((userData: GetServiceBusUserDetails) => {
          this.user = userData;
        }),
      )
      .subscribe();
  }

  downloadPdf() {
    this.overlayLoader.isLoading = true;

    let downloads = this.blobStorageService.downloadFile(`downloadPdf/ANB-${environment.tenant.toLocaleUpperCase()}.pdf`);

    if (this.authService.appToken !== '') {
      downloads = this.blobStorageService.downloadFileDirect(`downloadPdf/ANB-${environment.tenant.toLocaleUpperCase()}.pdf`);
    }

    return downloads.subscribe({
      next: (data) => {
        saveFile(data, `ANB_Energieportal_${environment.tenant.toLocaleUpperCase()}.pdf`);
        this.overlayLoader.isLoading = false;
      },
      error: (err) => {
        this.overlayLoader.isLoading = false;
        return err.message;
      },
    });
  }

  accept() {
    this.overlayLoader.isLoading = true;
    const userGuid = this.authService.currentUser.guid;

    this.userService
      .patch(userGuid, {
        legalDisclaimerVersion: this.latestLegalVersion,
        legalDisclaimerAcceptanceDate: moment.utc().format('YYYY-MM-DD[T]HH:mm:ss'),
      })

      .subscribe({
        next: () => {
          if (Object.values(GemisTenant).includes(environment.tenant as GemisTenant)) {
            this.userService
              .createServiceBusPayload({
                ...this.user,
                idpname: this.user.idpname,
                rolesCode: this.user.role.code,
                municipalities: this.userMunicipalitiesService.municipalities.map((municipality) => ({
                  name: municipality.name,
                  municipalityKey: municipality.key,
                })),
                action: ActionType.update,
                legalDisclaimerVersion: this.latestLegalVersion + ' ' + moment.utc().format('YYYY-MM-DD[T]HH:mm:ss'),
              })
              .pipe(
                mergeMap((payload) => {
                  return this.userService.postMessageToServiceBus(this.authService.currentUser.userId, payload);
                }),
              )
              .subscribe();
          }
          this.overlayLoader.isLoading = false;
          this.matDialogRef.close();
        },
        error: (err) => {
          this.overlayLoader.isLoading = false;
          return err.message;
        },
      });
  }

  decline() {
    this.overlayLoader.isLoading = true;
    this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.logout);
    setTimeout(() => {
      this.authService.logout();
    }, 2000);
  }
}
